/**
 * DeliveryMethodPickerDialog
 *
 * Presentational dialog for selecting the LOR delivery method.
 * - Premises path: select a property contact for certified mail
 * - Standard path: choose email/fax and enter recipient(s)
 */
import { Loader2 } from 'lucide-react';
import React from 'react';

import { formatMailingAddress } from '@/components/coverage/propertyContactAddress';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { PropertyContact } from '@/services/types/coverage-types';
import type { LorDeliveryMethod } from '@/services/types/document-types';

interface DeliveryMethodPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPremisesCoverage: boolean;
  isSubmitting: boolean;
  // Premises props
  propertyContacts: PropertyContact[];
  selectedPremisesMailContactIdx: number | null;
  onSelectPremisesContact: (idx: number) => void;
  // Standard props
  selectedDeliveryMethod: LorDeliveryMethod;
  onDeliveryMethodChange: (method: LorDeliveryMethod) => void;
  deliveryRecipients: string[];
  deliveryRecipientInput: string;
  onDeliveryRecipientInputChange: (value: string) => void;
  onAddRecipient: (value: string) => void;
  onRemoveRecipient: (value: string) => void;
  onRecipientKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  faxCoverTo: string;
  onFaxCoverToChange: (value: string) => void;
  faxCoverFrom: string;
  onFaxCoverFromChange: (value: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

export const DeliveryMethodPickerDialog: React.FC<
  DeliveryMethodPickerDialogProps
> = ({
  open,
  onOpenChange,
  isPremisesCoverage,
  isSubmitting,
  propertyContacts,
  selectedPremisesMailContactIdx,
  onSelectPremisesContact,
  selectedDeliveryMethod,
  onDeliveryMethodChange,
  deliveryRecipients,
  deliveryRecipientInput,
  onDeliveryRecipientInputChange,
  onAddRecipient,
  onRemoveRecipient,
  onRecipientKeyDown,
  faxCoverTo,
  onFaxCoverToChange,
  faxCoverFrom,
  onFaxCoverFromChange,
  onBack,
  onContinue,
}) => {
  const canContinue = isPremisesCoverage
    ? selectedPremisesMailContactIdx !== null
    : deliveryRecipients.length > 0 || Boolean(deliveryRecipientInput.trim());

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isSubmitting) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        className={isPremisesCoverage ? 'sm:max-w-2xl' : undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {isPremisesCoverage
              ? 'Select Certified Mail Recipient'
              : 'Save Delivery Details for Review'}
          </DialogTitle>
          <DialogDescription>
            {isPremisesCoverage
              ? 'Premises property coverages are sent by certified mail. Choose the property contact the managing paralegal should review and send to. We will prepare the certified-mail package now, but nothing will be mailed yet.'
              : "Choose how the reviewer should deliver the Letter of Representation and enter the recipient's contact information. This only saves the details for the Review & Send step and does not send anything yet."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {isPremisesCoverage && propertyContacts.length > 0 ? (
            <PremisesContactPicker
              propertyContacts={propertyContacts}
              selectedIdx={selectedPremisesMailContactIdx}
              onSelect={onSelectPremisesContact}
              isSubmitting={isSubmitting}
            />
          ) : (
            <StandardDeliveryPicker
              selectedDeliveryMethod={selectedDeliveryMethod}
              onDeliveryMethodChange={onDeliveryMethodChange}
              deliveryRecipients={deliveryRecipients}
              deliveryRecipientInput={deliveryRecipientInput}
              onDeliveryRecipientInputChange={onDeliveryRecipientInputChange}
              onAddRecipient={onAddRecipient}
              onRemoveRecipient={onRemoveRecipient}
              onRecipientKeyDown={onRecipientKeyDown}
              faxCoverTo={faxCoverTo}
              onFaxCoverToChange={onFaxCoverToChange}
              faxCoverFrom={faxCoverFrom}
              onFaxCoverFromChange={onFaxCoverFromChange}
            />
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            onClick={onContinue}
            disabled={isSubmitting || !canContinue}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparing certified mail…
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Sub-components ---------- */

const PremisesContactPicker: React.FC<{
  propertyContacts: PropertyContact[];
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
  isSubmitting: boolean;
}> = ({ propertyContacts, selectedIdx, onSelect, isSubmitting }) => {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-zinc-700">
        Certified mail recipient
      </p>
      <div className="max-h-[400px] overflow-y-auto space-y-3">
        {propertyContacts.map((contact, idx) => {
          const isSelected = selectedIdx === idx;
          const contactLabel =
            contact.name ||
            (contact.contact_type === 'PROPERTY_OWNER'
              ? 'Property Owner'
              : contact.contact_type === 'PROPERTY_MANAGER'
                ? 'Property Manager'
                : contact.custom_type_label || 'Contact');
          const formattedMailAddress =
            formatMailingAddress(contact.mailing_address) || contact.address;
          const hasMailingAddress = Boolean(contact.mailing_address);

          return (
            <div
              key={idx}
              className={`rounded-lg border p-4 transition ${
                isSelected
                  ? 'border-blue-500 bg-blue-50/50'
                  : 'border-zinc-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="premises-certified-mail-contact"
                  id={`premises-contact-${idx}`}
                  checked={isSelected}
                  onChange={() => onSelect(idx)}
                  disabled={isSubmitting}
                  className="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 space-y-2">
                  <label
                    htmlFor={`premises-contact-${idx}`}
                    className="block cursor-pointer"
                  >
                    <p className="text-sm font-semibold text-zinc-800">
                      {contactLabel}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {contact.contact_type === 'PROPERTY_OWNER'
                        ? 'Property Owner'
                        : contact.contact_type === 'PROPERTY_MANAGER'
                          ? 'Property Manager'
                          : contact.custom_type_label || 'Other'}
                      {formattedMailAddress &&
                        ` • ${formattedMailAddress}`}
                    </p>
                  </label>
                  {!hasMailingAddress && (
                    <p className="text-xs text-amber-600 mt-1">
                      Add a mailing address before this contact can
                      be used for certified mail.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {propertyContacts.length === 0 && (
        <p className="text-sm text-zinc-500 py-4 text-center">
          No property contacts have been added yet. Add contacts in
          the Research Property step.
        </p>
      )}
    </div>
  );
};

const StandardDeliveryPicker: React.FC<{
  selectedDeliveryMethod: LorDeliveryMethod;
  onDeliveryMethodChange: (method: LorDeliveryMethod) => void;
  deliveryRecipients: string[];
  deliveryRecipientInput: string;
  onDeliveryRecipientInputChange: (value: string) => void;
  onAddRecipient: (value: string) => void;
  onRemoveRecipient: (value: string) => void;
  onRecipientKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  faxCoverTo: string;
  onFaxCoverToChange: (value: string) => void;
  faxCoverFrom: string;
  onFaxCoverFromChange: (value: string) => void;
}> = ({
  selectedDeliveryMethod,
  onDeliveryMethodChange,
  deliveryRecipients,
  deliveryRecipientInput,
  onDeliveryRecipientInputChange,
  onAddRecipient,
  onRemoveRecipient,
  onRecipientKeyDown,
  faxCoverTo,
  onFaxCoverToChange,
  faxCoverFrom,
  onFaxCoverFromChange,
}) => {
  return (
    <>
      <div className="space-y-3">
        <p className="text-sm font-medium text-zinc-700">
          Delivery method
        </p>
        <RadioGroup
          value={selectedDeliveryMethod}
          onValueChange={(val) =>
            onDeliveryMethodChange(val as LorDeliveryMethod)
          }
          className="grid gap-3 sm:grid-cols-2"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={() => onDeliveryMethodChange('email')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onDeliveryMethodChange('email');
              }
            }}
            className={`cursor-pointer rounded-lg border p-3 text-left transition ${
              selectedDeliveryMethod === 'email'
                ? 'border-blue-500 bg-blue-50/70 text-blue-900'
                : 'border-zinc-200 bg-white text-zinc-800'
            }`}
          >
            <div className="flex items-start gap-3">
              <RadioGroupItem id="delivery-email" value="email" />
              <div>
                <p className="text-sm font-semibold">Email</p>
                <p className="text-xs text-zinc-500">
                  Send via email using Front
                </p>
              </div>
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => onDeliveryMethodChange('fax')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onDeliveryMethodChange('fax');
              }
            }}
            className={`cursor-pointer rounded-lg border p-3 text-left transition ${
              selectedDeliveryMethod === 'fax'
                ? 'border-blue-500 bg-blue-50/70 text-blue-900'
                : 'border-zinc-200 bg-white text-zinc-800'
            }`}
          >
            <div className="flex items-start gap-3">
              <RadioGroupItem id="delivery-fax" value="fax" />
              <div>
                <p className="text-sm font-semibold">Fax</p>
                <p className="text-xs text-zinc-500">
                  Send via fax through HelloSign
                </p>
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>
      <div className="space-y-2">
        <Label>
          {selectedDeliveryMethod === 'email'
            ? 'Recipients'
            : 'Fax numbers'}
        </Label>
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500">
          {deliveryRecipients.map((recipient) => (
            <span
              key={recipient}
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-sm text-blue-700"
            >
              <span className="max-w-[180px] truncate">
                {recipient}
              </span>
              <button
                type="button"
                onClick={() => onRemoveRecipient(recipient)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-blue-100"
                aria-label={`Remove ${recipient}`}
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
          <input
            type={
              selectedDeliveryMethod === 'email' ? 'email' : 'tel'
            }
            value={deliveryRecipientInput}
            onChange={(e) =>
              onDeliveryRecipientInputChange(e.target.value)
            }
            onKeyDown={onRecipientKeyDown}
            onBlur={() => {
              if (deliveryRecipientInput.trim()) {
                onAddRecipient(deliveryRecipientInput);
              }
            }}
            placeholder={(() => {
              if (deliveryRecipients.length > 0) return '';
              return selectedDeliveryMethod === 'email'
                ? 'adjuster@example.com'
                : '+1 (555) 123-4567';
            })()}
            className="min-w-[120px] flex-1 border-none bg-transparent py-1 text-sm outline-none placeholder:text-zinc-400"
          />
        </div>
        <p className="text-xs text-zinc-500">
          Press Enter, Tab, or comma to add multiple{' '}
          {selectedDeliveryMethod === 'email'
            ? 'addresses'
            : 'numbers'}
        </p>
      </div>

      {/* Fax cover page fields - only shown when fax is selected */}
      {selectedDeliveryMethod === 'fax' && (
        <div className="space-y-3 border-t border-zinc-100 pt-4">
          <p className="text-sm font-medium text-zinc-700">
            Cover Page Details
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label
                htmlFor="fax-cover-to-picker"
                className="text-xs"
              >
                To
              </Label>
              <Input
                id="fax-cover-to-picker"
                type="text"
                placeholder="Claims Department"
                value={faxCoverTo}
                onChange={(e) => onFaxCoverToChange(e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor="fax-cover-from-picker"
                className="text-xs"
              >
                From
              </Label>
              <Input
                id="fax-cover-from-picker"
                type="text"
                placeholder="Your Name / Law Firm"
                value={faxCoverFrom}
                onChange={(e) => onFaxCoverFromChange(e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
